import React from 'react';
import { useSelector } from 'react-redux';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
// import BuildIcon from '@material-ui/icons/Build';

const ANNOUNCEMENT_BANNER_DISMISSED_KEY = 'consumerAnnouncementBannerDismissed';

const ANNOUNCEMENT_BANNER_COLORS = {
  green: {
    background: '#e8f7f0',
    border: '#17b471',
    title: '#0b8f59',
    iconBackground: '#d4f1e4',
  },
  yellow: {
    background: '#fff8e1',
    border: '#f6c343',
    title: '#8a6500',
    iconBackground: '#fff0b8',
  },
  red: {
    background: '#fdeaea',
    border: '#d93025',
    title: '#c5221f',
    iconBackground: '#fbd3d0',
  },
};

const normalizeAnnouncementBannerColor = (color) => {
  const colorMap = {
    success: 'green',
    warning: 'yellow',
    error: 'red',
    danger: 'red',
  };

  return colorMap[color] || color || 'green';
};

const formatAnnouncementBannerDate = (value) => {
  if (!value) {
    return '';
  }

  return String(value).slice(0, 10);
};

const normalizeAnnouncementBanner = (announcementBanner) => {
  if (!announcementBanner) {
    return null;
  }

  return {
    active: Boolean(announcementBanner.active),
    startDate: formatAnnouncementBannerDate(announcementBanner.startDate),
    endDate: formatAnnouncementBannerDate(announcementBanner.endDate),
    title: announcementBanner.title || '',
    message: announcementBanner.message || '',
    color: normalizeAnnouncementBannerColor(announcementBanner.color),
  };
};

const isAnnouncementBannerActive = (announcementBanner) => {
  const normalizedBanner = normalizeAnnouncementBanner(announcementBanner);

  if (!normalizedBanner || !normalizedBanner.active) {
    return false;
  }

  if (!normalizedBanner.title && !normalizedBanner.message) {
    return false;
  }

  return true;
};

const getAnnouncementBannerSessionValue = (announcementBanner) => {
  const normalizedBanner = normalizeAnnouncementBanner(announcementBanner);

  if (!normalizedBanner) {
    return '';
  }

  return [
    normalizedBanner.active,
    normalizedBanner.startDate,
    normalizedBanner.endDate,
    normalizedBanner.title,
    normalizedBanner.message,
    normalizedBanner.color,
  ].join('|');
};

const getAnnouncementBannerFromState = (state) => {
  const globalSettings = state.globalSettings || {};

  return (
    globalSettings.announcementBanner ||
    globalSettings.data?.announcementBanner ||
    globalSettings.settings?.announcementBanner ||
    globalSettings.globalSettingsData?.announcementBanner ||
    globalSettings.globalSettings?.announcementBanner ||
    null
  );
};

const getAuthSessionFromState = (state) => {
  return state.auth?.accessToken || state.auth?.idToken || '';
};

const getDismissedStorageValue = (authSession, announcementBannerSessionValue) => {
  if (!authSession || !announcementBannerSessionValue) {
    return '';
  }

  return `${authSession}|${announcementBannerSessionValue}`;
};

const AnnouncementBanner = () => {
  const announcementBanner = useSelector(getAnnouncementBannerFromState);
  const authSession = useSelector(getAuthSessionFromState);
  const [dismissedAnnouncementBanner, setDismissedAnnouncementBanner] = React.useState(() =>
    localStorage.getItem(ANNOUNCEMENT_BANNER_DISMISSED_KEY)
  );

  React.useEffect(() => {
    const syncDismissedBanner = () => {
      setDismissedAnnouncementBanner(localStorage.getItem(ANNOUNCEMENT_BANNER_DISMISSED_KEY));
    };

    window.addEventListener('storage', syncDismissedBanner);

    return () => {
      window.removeEventListener('storage', syncDismissedBanner);
    };
  }, []);

  const normalizedBanner = React.useMemo(() => {
    return normalizeAnnouncementBanner(announcementBanner);
  }, [announcementBanner]);

  const announcementBannerSessionValue = React.useMemo(() => {
    return getAnnouncementBannerSessionValue(announcementBanner);
  }, [announcementBanner]);

  const dismissedStorageValue = React.useMemo(() => {
    return getDismissedStorageValue(authSession, announcementBannerSessionValue);
  }, [authSession, announcementBannerSessionValue]);

  const showAnnouncementBanner =
    isAnnouncementBannerActive(announcementBanner) && dismissedAnnouncementBanner !== dismissedStorageValue;

  const closeAnnouncementBanner = React.useCallback(() => {
    localStorage.setItem(ANNOUNCEMENT_BANNER_DISMISSED_KEY, dismissedStorageValue);
    setDismissedAnnouncementBanner(dismissedStorageValue);
  }, [dismissedStorageValue]);

  if (!showAnnouncementBanner || !normalizedBanner) {
    return null;
  }

  const colors = ANNOUNCEMENT_BANNER_COLORS[normalizedBanner.color] || ANNOUNCEMENT_BANNER_COLORS.green;

  return (
    <div
      style={{
        background: colors.background,
        borderBottom: `1px solid ${colors.border}`,
        padding: '18px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          minWidth: 0,
        }}
      >
        {/* <div
          style={{
            width: '42px',
            height: '42px',
            minWidth: '42px',
            borderRadius: '50%',
            background: colors.iconBackground,
            color: colors.title,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '18px',
          }}
        >
          <BuildIcon fontSize="small" />
        </div> */}

        <div
          style={{
            minWidth: 0,
            paddingRight: '24px',
          }}
        >
          {normalizedBanner.title && (
            <div
              style={{
                color: colors.title,
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: 1.4,
                marginBottom: normalizedBanner.message ? '4px' : 0,
              }}
            >
              {normalizedBanner.title}
            </div>
          )}

          {normalizedBanner.message && (
            <div
              style={{
                color: '#1f2937',
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: 1.5,
                whiteSpace: 'pre-line',
              }}
            >
              {normalizedBanner.message}
            </div>
          )}
        </div>
      </div>

      <IconButton
        size="small"
        onClick={closeAnnouncementBanner}
        style={{
          color: colors.title,
          padding: '4px',
          marginTop: '4px',
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

export default AnnouncementBanner;
